import { create } from 'zustand';
import { Session, User, Subscription } from '@supabase/supabase-js';
import { supabase } from './supabase';

let authSubscription: Subscription | null = null;

export type Role = 'admin' | 'reseller';

export interface Reseller {
  id: string;
  name: string;
  discount_percent: number;
  margin_percent: number;
  default_language: string;
  domain: string | null;
  brand_color: string | null;
  email: string | null;
  address_street: string;
  address_city: string;
  address_postal_code: string;
  address_country: string;
  cart_url: string | null;
  cart_api_key_id: string | null;
  shop_url: string | null;
}

export interface Profile {
  id: string;
  role: Role;
  reseller_id: string | null;
}

interface AuthState {
  session: Session | null;
  user: User | null;
  profile: Profile | null;
  reseller: Reseller | null;
  loading: boolean;

  initialize: () => Promise<void>;
  signIn: (email: string, password: string) => Promise<{ error: string | null; role?: Role }>;
  signOut: () => Promise<void>;
  refreshProfile: () => Promise<void>;
}

async function loadProfile(userId: string): Promise<{ profile: Profile | null; reseller: Reseller | null }> {
  const { data: profile, error } = await supabase
    .from('profiles')
    .select('id, role, reseller_id')
    .eq('id', userId)
    .maybeSingle();

  if (error || !profile) return { profile: null, reseller: null };

  let reseller: Reseller | null = null;
  if (profile.reseller_id) {
    const { data } = await supabase
      .from('resellers')
      .select('id, name, discount_percent, margin_percent, default_language, domain, brand_color, email, address_street, address_city, address_postal_code, address_country, cart_url, cart_api_key_id, shop_url')
      .eq('id', profile.reseller_id)
      .maybeSingle();
    reseller = data ?? null;
  }

  return { profile, reseller };
}

export const useAuthStore = create<AuthState>((set, get) => ({
  session: null,
  user: null,
  profile: null,
  reseller: null,
  loading: true,

  initialize: async () => {
    if (authSubscription) return;

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      (async () => {
        if (!session?.user) {
          set({ session: null, user: null, profile: null, reseller: null, loading: false });
          return;
        }

        const state = get();

        if (
          (event === 'TOKEN_REFRESHED' || event === 'USER_UPDATED') &&
          state.profile &&
          state.user?.id === session.user.id
        ) {
          set({ session, user: session.user });
          return;
        }

        if (event === 'SIGNED_IN' && state.profile && state.user?.id === session.user.id) {
          set({ session });
          return;
        }

        set({ loading: true });
        const { profile, reseller } = await loadProfile(session.user.id);
        set({ session, user: session.user, profile, reseller, loading: false });
      })();
    });

    authSubscription = subscription;

    const { data: { session } } = await supabase.auth.getSession();
    if (session?.user) {
      const { profile, reseller } = await loadProfile(session.user.id);
      set({ session, user: session.user, profile, reseller, loading: false });
    } else {
      set({ loading: false });
    }
  },

  signIn: async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) return { error: error.message };
    if (data.session?.user) {
      const { profile, reseller } = await loadProfile(data.session.user.id);
      set({ session: data.session, user: data.session.user, profile, reseller, loading: false });
      return { error: null, role: profile?.role };
    }
    return { error: null };
  },

  signOut: async () => {
    await supabase.auth.signOut();
    set({ session: null, user: null, profile: null, reseller: null });
  },

  refreshProfile: async () => {
    const { user } = get();
    if (!user) return;
    const { profile, reseller } = await loadProfile(user.id);
    set({ profile, reseller });
  },
}));
